import React, { useState } from 'react';
import { FaTrash, FaUsers, FaClock, FaCommentDots, FaPlay } from 'react-icons/fa';

const RoomSelectionModal = ({ 
  isOpen, 
  onClose, 
  rooms, 
  narrativeName,
  onJoinRoom, 
  onDeleteRoom, 
  onCreateNew 
}) => {
  const [deletingRooms, setDeletingRooms] = useState(new Set());

  if (!isOpen) return null;

  const handleDeleteRoom = async (roomId, roomName) => {
    if (!window.confirm(`Are you sure you want to delete "${roomName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingRooms(prev => new Set([...prev, roomId]));
    try {
      await onDeleteRoom(roomId);
    } catch (error) {
      console.error('Error deleting room:', error);
      alert('Failed to delete room. Please try again.');
    } finally {
      setDeletingRooms(prev => {
        const next = new Set(prev);
        next.delete(roomId);
        return next;
      });
    }
  };

  const formatLastActivity = (lastActivity) => {
    if (!lastActivity) return 'Unknown';
    
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffInHours = Math.floor((now - activity) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than 1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="bg-amber-600 text-white p-4">
          <h2 className="text-xl font-bold">Select Your {narrativeName} Adventure</h2>
          <p className="text-amber-100 text-sm mt-1">
            You have existing rooms for this story. Choose one to continue or create a new adventure.
          </p>
        </div>

        <div className="overflow-y-auto max-h-[60vh] p-4">
          {rooms.map((room) => (
            <div 
              key={room.id} 
              onClick={() => onJoinRoom(room)}
              className="border border-gray-200 rounded-lg p-4 mb-3 hover:bg-amber-50 hover:border-amber-300 transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 group-hover:text-amber-800 transition-colors">📖 {room.name}</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded group-hover:bg-amber-100 group-hover:text-amber-800 transition-colors">
                      Scene {room.scene_progress}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600 mb-3 group-hover:text-amber-700 transition-colors">
                    <div className="flex items-center gap-1">
                      <FaClock className="w-4 h-4" />
                      <span>{formatLastActivity(room.last_activity)}</span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <FaCommentDots className="w-4 h-4" />
                      <span>{room.message_count} messages</span>
                    </div>
                    
                    {room.participant_names && room.participant_names.length > 0 && (
                      <div className="flex items-center gap-1">
                        <FaUsers className="w-4 h-4" />
                        <span>with {room.participant_names.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors">
                    Room Code: {room.room_code} • Created {formatLastActivity(room.created_at)} • <strong>Click to continue</strong>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      onJoinRoom(room);
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-colors"
                  >
                    <FaPlay className="w-4 h-4" />
                    Continue
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      handleDeleteRoom(room.id, room.name);
                    }}
                    disabled={deletingRooms.has(room.id)}
                    className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white p-2 rounded-md transition-colors"
                    title="Delete this room permanently"
                  >
                    {deletingRooms.has(room.id) ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaTrash className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t bg-gray-50 p-4 flex justify-between items-center">
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded-md transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={onCreateNew}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            Create New Adventure
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomSelectionModal;
